import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import AdminShipFromLocationSelect from './AdminShipFromLocationSelect';
import { buildLabelPreviewHtml, printShippingLabel } from './printShippingLabel';
import {
  configToPrintOptions,
  locationToSender,
  mergeShipFromConfig,
  newEmptyLocation,
} from './shipFromLocationsUtils';
import { AdminPanelAlert, AdminPanelHero, AdminPanelSection } from './AdminPanelUi';
import { useSaveButtonLabel } from './useSaveButtonLabel';

const SAMPLE_ORDER = {
  order_number: 'PREVIEW',
  shipping: {
    name: 'Sample Customer',
    phone: '9876543210',
    address: '12 Sample Street, Near City Mall',
    city: 'Mumbai',
    state: 'Maharashtra',
    pincode: '400001',
  },
  items: [{ quantity: 2 }],
};

function LocationCard({ loc, index, isDefault, onChange, onRemove, onSetDefault, canRemove }) {
  const set = (key, val) => onChange(index, { ...loc, [key]: val });

  return (
    <div className="admin-panel-section mb-3">
      <div className="admin-panel-section__head d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="radio"
            name="ship-from-default"
            id={`ship-from-def-${index}`}
            checked={isDefault}
            onChange={() => onSetDefault(index)}
          />
          <label className="form-check-label small fw-semibold" htmlFor={`ship-from-def-${index}`}>
            Default for new prints
          </label>
        </div>
        {canRemove ? (
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onRemove(index)}>
            Remove
          </button>
        ) : null}
      </div>
      <div className="admin-panel-section__body">
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label small mb-1">Location name</label>
            <input
              className="form-control form-control-sm"
              value={loc.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder="e.g. Pune warehouse"
              required
            />
            <div className="form-text">Shown in Orders → Print label dropdown.</div>
          </div>
          <div className="col-md-4">
            <label className="form-label small mb-1">Sender name (on label)</label>
            <input
              className="form-control form-control-sm"
              value={loc.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Saloni Designs"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label small mb-1">Phone</label>
            <input
              className="form-control form-control-sm"
              value={loc.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="9680384332"
            />
          </div>
          <div className="col-12">
            <label className="form-label small mb-1">Address line 1</label>
            <input
              className="form-control form-control-sm"
              value={loc.address_line1}
              onChange={(e) => set('address_line1', e.target.value)}
              placeholder="Shop / building / street"
            />
          </div>
          <div className="col-12">
            <label className="form-label small mb-1">Address line 2</label>
            <input
              className="form-control form-control-sm"
              value={loc.address_line2}
              onChange={(e) => set('address_line2', e.target.value)}
              placeholder="Area, landmark (optional)"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label small mb-1">City</label>
            <input
              className="form-control form-control-sm"
              value={loc.city}
              onChange={(e) => set('city', e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label small mb-1">State</label>
            <input
              className="form-control form-control-sm"
              value={loc.state}
              onChange={(e) => set('state', e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label small mb-1">PIN code</label>
            <input
              className="form-control form-control-sm"
              value={loc.pincode}
              onChange={(e) => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSiteShipFromFields({ sessionToken, setStatus }) {
  const [config, setConfig] = useState(() => mergeShipFromConfig(null));
  const [columnReady, setColumnReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const saveBtn = useSaveButtonLabel('Save ship-from locations');
  const [previewIdx, setPreviewIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await fetch('/api/admin_ship_from_locations.php', { headers: adminAuthHeaders(sessionToken) });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setLoadError(d.error || 'Could not load ship-from locations.');
        return;
      }
      setColumnReady(Boolean(d.column_ready));
      setConfig(mergeShipFromConfig(d.config));
    } catch (e) {
      setLoadError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const updateLoc = (index, loc) => {
    setConfig((c) => {
      const locations = [...c.locations];
      locations[index] = loc;
      return { ...c, locations };
    });
  };

  const setDefault = (index) => {
    setConfig((c) => {
      const id = c.locations[index]?.id;
      return {
        ...c,
        default_id: id || c.default_id,
        locations: c.locations.map((loc, i) => ({ ...loc, is_default: i === index })),
      };
    });
  };

  const addLocation = () => {
    setConfig((c) => ({
      ...c,
      locations: [...c.locations, newEmptyLocation()],
    }));
  };

  const removeLocation = (index) => {
    setConfig((c) => {
      if (c.locations.length <= 1) return c;
      const locations = c.locations.filter((_, i) => i !== index);
      let default_id = c.default_id;
      if (!locations.some((l) => l.id === default_id)) {
        default_id = locations[0]?.id || '';
        locations[0] = { ...locations[0], is_default: true };
      }
      return { ...c, locations, default_id };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const invalid = config.locations.some((l) => !String(l.label || '').trim());
    if (invalid) {
      setStatus('Each location needs a name (e.g. Pune).');
      return;
    }
    saveBtn.startSaving();
    setStatus('');
    try {
      const r = await fetch('/api/admin_ship_from_locations.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({ session_token: sessionToken, config }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setStatus(d.error || 'Could not save.');
        saveBtn.markFailed();
        return;
      }
      setConfig(mergeShipFromConfig(d.config));
      setStatus('Ship-from locations saved.');
      saveBtn.markSaved();
    } catch (err) {
      setStatus(err.message || 'Save failed.');
      saveBtn.markFailed();
    }
  };

  const previewSender = useMemo(() => {
    const loc = config.locations[previewIdx] || config.locations[0];
    return loc ? locationToSender(loc) : null;
  }, [config, previewIdx]);

  const previewSelectOptions = useMemo(
    () =>
      config.locations.map((loc, i) => ({
        id: String(i),
        label: loc.label || `Location ${i + 1}`,
      })),
    [config.locations]
  );

  const previewHtml = useMemo(() => {
    if (!previewSender) return '';
    return buildLabelPreviewHtml(SAMPLE_ORDER, previewSender, config.label_size, true);
  }, [previewSender, config.label_size]);

  if (loading) {
    return <div className="alert alert-info">Loading shipping label settings…</div>;
  }

  return (
    <form onSubmit={save} className="admin-ship-from-settings admin-panel-workspace-root">
      <AdminPanelHero
        title="Shipping labels"
        lead="Ship-from addresses used when printing packing labels from Orders."
      />

      {loadError ? <AdminPanelAlert variant="danger">{loadError}</AdminPanelAlert> : null}
      {!columnReady ? (
        <AdminPanelAlert variant="warning">
          Label settings are not available yet on this server. Contact support or reload after your store database is
          updated.
        </AdminPanelAlert>
      ) : null}

      <AdminPanelSection
        title="Print format"
        subtitle="Choose label size for thermal printers or A4 sheets."
      >
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label small" htmlFor="label-size">
                Print format
              </label>
              <select
                id="label-size"
                className="form-select form-select-sm"
                value={config.label_size}
                onChange={(e) => setConfig((c) => ({ ...c, label_size: e.target.value }))}
              >
                <option value="thermal_4x6">Thermal 4×6 in (100×150 mm) — recommended</option>
                <option value="standard">A4 paper — 6 labels per sheet (batch) or cut single</option>
              </select>
            </div>
          </div>
      </AdminPanelSection>

      {config.locations.map((loc, index) => (
        <LocationCard
          key={`${loc.id}-${index}`}
          loc={loc}
          index={index}
          isDefault={loc.id === config.default_id || loc.is_default}
          onChange={updateLoc}
          onRemove={removeLocation}
          onSetDefault={setDefault}
          canRemove={config.locations.length > 1}
        />
      ))}

      <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={addLocation}>
        + Add location
      </button>

      <div className="row g-3 mb-3">
        <div className="col-lg-6">
          <div className="card border shadow-sm h-100">
            <div className="card-header py-2 bg-white d-flex justify-content-between align-items-center gap-2">
              <span className="small fw-semibold flex-shrink-0">Label preview</span>
              <AdminShipFromLocationSelect
                className="ms-auto"
                value={String(previewIdx)}
                options={previewSelectOptions}
                aria-label="Preview location"
                onChange={(e) => setPreviewIdx(Number(e.target.value))}
              />
            </div>
            <div className="card-body admin-ship-from-preview-wrap">
              {previewHtml ? (
                <iframe
                  title="Label preview"
                  className="admin-ship-from-preview-frame"
                  srcDoc={previewHtml}
                  sandbox="allow-same-origin"
                />
              ) : null}
            </div>
          </div>
        </div>
        <div className="col-lg-6 d-flex align-items-end">
          <div className="small text-muted">
            <p className="mb-2">
              <strong>Tips:</strong> Mark your busiest warehouse as default. Pune and Ahmedabad can each have a
              complete return address.
            </p>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!previewSender}
              onClick={() =>
                printShippingLabel(SAMPLE_ORDER, previewSender, config.label_size)
              }
            >
              Test print sample
            </button>
          </div>
        </div>
      </div>

      <div className="admin-panel-savebar">
        <button
          type="submit"
          className={`btn${saveBtn.isSaved ? ' btn-success' : ' btn-primary'}`}
          disabled={saveBtn.isSaving || !columnReady}
        >
          {saveBtn.label}
        </button>
      </div>
    </form>
  );
}
